import {atom, DefaultValue, selector} from "recoil";
import {ConstructorSize} from "../types/ConstructorSize";
import {ConstructorModel} from "../types/ConstructorModel";
import {OptionsSelected} from "../types/OptionsSelected";
import {stateAccessoriesBlocks, stateComplectationBlocks} from "./stateBlocks";
import {ComplectationBlock} from "../types/ComplectationBlock";
import {splitUid} from "../../utils/ApplicationUtils";


export interface StateSelectedElements {
    model: ConstructorModel | null
    size: ConstructorSize | null
    complectations: OptionsSelected
    accesories: OptionsSelected
}

export const stateSelectedDefaults: StateSelectedElements = {
    model: null,
    size: null,
    complectations: {},
    accesories: {},
}

/**
 * State for all selected elements in the app
 */
export const stateSelectedElements = atom<StateSelectedElements>({
    key: 'selectedElements',
    default: (window.__CONSTRUCTOR_STATE__ as StateSelectedElements) || stateSelectedDefaults,
});


/**
 * Calculated state for selected model
 * based on all selected elements state
 */
export const stateSelectedModel = selector<StateSelectedElements["model"]>({
    key: 'selectedModel',
    get: ({get}) => {
        const activeElements = get(stateSelectedElements);

        return activeElements.model;
    },
    set: ({set}, model) => {
        set(
            stateSelectedElements,
            (prevValue) => model instanceof DefaultValue ? model : ({
                ...prevValue,
                model,
                size: null,
                complectations: {},
                accessories: {},
            })
        );
    }
});

/**
 * Calculated state for active size
 * based on all active elements state
 */
export const stateSelectedSize = selector<StateSelectedElements['size']>({
    key: 'selectedSize',
    get: ({get}) => {
        const elements = get(stateSelectedElements);

        return elements.size;
    },
    set: ({set}, size) => {
        set(
            stateSelectedElements,
            (prevValue) => size instanceof DefaultValue ? size : ({
                ...prevValue,
                size,
                complectations: {},
                accessories: {},
            })
        );
    }
});


/**
 * Calculated state for selected options
 * based on all active elements state
 */
export const stateSelectedComplectations = selector<StateSelectedElements['complectations']>({
    key: 'selectedComplectations',
    get: ({get}) => {
        const elements = get(stateSelectedElements);

        return elements.complectations;
    },
    set: ({set, get}, selected) => {
        const blocks = get(stateComplectationBlocks);

        set(
            stateSelectedElements,
            (prevValue) => {
                if (selected instanceof DefaultValue) {
                    return selected;
                } else {
                    return ({
                        ...prevValue,
                        complectations: removeSelectedWithoutParentDependency(blocks, selected),
                    });
                }
            }
        );
    }
});

export const stateSelectedAccessories = selector<StateSelectedElements['complectations']>({
    key: 'selectedAccesories',
    get: ({get}) => {
        const elements = get(stateSelectedElements);

        return elements.accesories;
    },
    set: ({set, get}, selected) => {
        const blocks = get(stateAccessoriesBlocks);

        set(
            stateSelectedElements,
            (prevValue) => {
                if (selected instanceof DefaultValue) {
                    return selected;
                } else {
                    return ({
                        ...prevValue,
                        accesories: removeSelectedWithoutParentDependency(blocks, selected),
                    });
                }
            }
        );
    }
});

const removeSelectedWithoutParentDependency = (blocks: ComplectationBlock[], selected: OptionsSelected) => {
    return Object.keys(selected).reduce((total, uid) => {

        const dependencies = selected[uid]?.dependencies;

        if (!dependencies) {
            return total;
        }

        /**
         * Если атрибут скрытый, то не нужно его удалять
         *
         * Например для Mix-основ ремень не должен сбрасываться, если выбрали лого
         */
        const [blockUid, attributeUid] = splitUid(uid);
        const block = blocks.find(el => el.id === blockUid);
        const attribute = block?.attributes.find(el => el.id === attributeUid);

        if (attribute?.isHidden) {
            return total;
        }

        for (const dependencyKey in dependencies) {
            const id = total[dependencyKey]?.id || null;

            if (!id || !dependencies[dependencyKey].includes(id)) {
                delete total[uid];
            }
        }

        return total;
    }, {...selected});
};
